import type { DbClient } from '@fintree/supabase/db-client'
import { defaultDbClient } from '@fintree/supabase/db-client'
import type {
  Candidate,
  CandidateWithStudent,
  PortalProfileFields,
  HubProfileFields,
  StudentWithCandidates,
} from './dto'
import type { Database } from '../types'

type StudentProfileRow = Database['public']['Tables']['student_profiles']['Row']

export function calculateProfileScore(candidate: Partial<Candidate>): number {
  let score = 0
  if (candidate.current_position?.trim()) score += 20
  if (candidate.experience_years != null && candidate.experience_years !== undefined) score += 10
  if (candidate.education?.trim()) score += 15
  if (candidate.skills?.trim()) score += 15
  if (candidate.resume_url?.trim()) score += 25
  if (candidate.linkedin_url?.trim()) score += 15
  return Math.min(score, 100)
}

function pickCandidate(embed: Candidate | Candidate[] | null | undefined): Candidate | null {
  if (embed == null) return null
  if (Array.isArray(embed)) return embed[0] ?? null
  return embed
}

function pickStudentProfile(
  embed: StudentProfileRow | StudentProfileRow[] | null | undefined
): StudentProfileRow | null {
  if (embed == null) return null
  if (Array.isArray(embed)) return embed[0] ?? null
  return embed
}

export function computePortalCompletionPercent(
  portalProfile: PortalProfileFields | null,
  profile: HubProfileFields | null,
  student: { city: string | null }
): number | null {
  const hasPortalData = Boolean(
    portalProfile?.company ||
      portalProfile?.role ||
      portalProfile?.city ||
      portalProfile?.graduation_year ||
      portalProfile?.linkedin_url ||
      portalProfile?.avatar_url ||
      portalProfile?.exp_band
  )
  if (!hasPortalData && !profile) return null

  const fields: unknown[] = [
    portalProfile?.avatar_url ?? null,
    portalProfile?.city ?? student.city ?? null,
    portalProfile?.graduation_year ?? null,
    portalProfile?.linkedin_url ?? null,
    portalProfile?.company ?? null,
    portalProfile?.role ?? null,
    portalProfile?.exp_band ?? null,
    profile?.resume_url ?? null,
    profile?.skills ?? null,
    profile?.education ?? null,
    profile?.experience_years ?? null,
    profile?.current_position ?? null,
  ]

  const filled = fields.filter((v) => {
    if (v == null) return false
    if (typeof v === 'string') return v.trim().length > 0
    if (typeof v === 'number') return true
    return false
  }).length

  return Math.round((filled / fields.length) * 100)
}

function mapStudentRow(s: StudentWithCandidates): CandidateWithStudent {
  const profile = pickCandidate(s.candidates)
  const portalProfile = pickStudentProfile(s.student_profiles)
  const hasProfile = Boolean(profile?.id)
  const placementDefault = profile?.placement_status ?? 'seeking'

  return {
    id: profile?.id ?? null,
    phone: s.phone,
    name: s.name || profile?.name || '',
    current_position: profile?.current_position ?? null,
    experience_years: profile?.experience_years ?? null,
    exp_band: profile?.exp_band ?? s.exp_band ?? null,
    education: profile?.education ?? null,
    skills: profile?.skills ?? null,
    resume_url: profile?.resume_url ?? null,
    linkedin_url: profile?.linkedin_url ?? null,
    availability: profile?.availability ?? null,
    placement_status: placementDefault,
    profile_score: profile?.profile_score ?? 0,
    reliability_score: profile?.reliability_score ?? 100,
    total_applications: profile?.total_applications ?? 0,
    total_shortlists: profile?.total_shortlists ?? 0,
    total_placements: profile?.total_placements ?? 0,
    created_at: profile?.created_at ?? null,
    updated_at: profile?.updated_at ?? null,
    hasProfile,
    student: {
      name: s.name,
      course: s.course,
      attempt: s.attempt,
      city: s.city,
      email: s.email,
    },
    portal_company: portalProfile?.company ?? null,
    portal_role: portalProfile?.role ?? null,
    portal_city: portalProfile?.city ?? null,
    portal_graduation_year: portalProfile?.graduation_year ?? null,
    portal_linkedin_url: portalProfile?.linkedin_url ?? null,
    portal_avatar_url: portalProfile?.avatar_url ?? null,
    portal_exp_band: portalProfile?.exp_band ?? null,
    portal_completion_percent: computePortalCompletionPercent(portalProfile, profile, s),
  }
}

export interface CandidatesPaginatedResult {
  rows: CandidateWithStudent[]
  total: number
  page: number
  page_size: number
}

interface CandidateRpcRow {
  candidate_id?: string | null
  phone?: string | null
  name?: string | null
  current_position?: string | null
  experience_years?: number | string | null
  exp_band?: string | null
  education?: string | null
  skills?: string | null
  resume_url?: string | null
  linkedin_url?: string | null
  availability?: string | null
  placement_status?: string | null
  course?: string | null
  city?: string | null
  email?: string | null
  portal_company?: string | null
  portal_role?: string | null
  portal_city?: string | null
  portal_graduation_year?: number | null
  portal_linkedin_url?: string | null
  portal_avatar_url?: string | null
  portal_exp_band?: string | null
  portal_completion_percent?: number | null
  portal_completed_wizard?: boolean | null
}

interface CandidatesPaginatedRpcResult {
  rows: CandidateRpcRow[]
  total: number
  page: number
  page_size: number
}

export async function getCandidatesPaginated(
  page = 0,
  pageSize = 50,
  search?: string,
  placementStatus?: string[],
  expBand?: string[],
  client: DbClient = defaultDbClient
): Promise<CandidatesPaginatedResult> {
  const { data, error } = await client.rpc('get_candidates_paginated', {
    p_page: page,
    p_page_size: pageSize,
    p_search: search || undefined,
    p_placement_status: placementStatus || undefined,
    p_exp_band: expBand || undefined,
  })
  if (error) throw error

  const raw = (data as CandidatesPaginatedRpcResult | null) ?? {
    rows: [],
    total: 0,
    page,
    page_size: pageSize,
  }

  const rows: CandidateWithStudent[] = raw.rows.map((r) => ({
    id: r.candidate_id ?? null,
    phone: r.phone ?? '',
    name: r.name ?? '',
    current_position: r.current_position ?? null,
    experience_years: r.experience_years != null ? Number(r.experience_years) : null,
    exp_band: r.exp_band ?? null,
    education: r.education ?? null,
    skills: r.skills ?? null,
    resume_url: r.resume_url ?? null,
    linkedin_url: r.linkedin_url ?? null,
    availability: r.availability ?? null,
    placement_status: r.placement_status ?? null,
    profile_score: 0,
    reliability_score: 0,
    total_applications: 0,
    total_shortlists: 0,
    total_placements: 0,
    created_at: null,
    updated_at: null,
    hasProfile: Boolean(r.current_position || r.experience_years != null || r.education || r.skills),
    student: {
      name: r.name ?? null,
      course: r.course ?? null,
      attempt: null,
      city: r.city ?? null,
      email: r.email ?? null,
    },
    portal_company: r.portal_company ?? null,
    portal_role: r.portal_role ?? null,
    portal_city: r.portal_city ?? null,
    portal_graduation_year: r.portal_graduation_year ?? null,
    portal_linkedin_url: r.portal_linkedin_url ?? null,
    portal_avatar_url: r.portal_avatar_url ?? null,
    portal_exp_band: r.portal_exp_band ?? null,
    portal_completion_percent: r.portal_completion_percent ?? null,
  }))

  return { rows, total: raw.total, page: raw.page, page_size: raw.page_size }
}

export async function getCandidateByPhone(
  phone: string,
  client: DbClient = defaultDbClient
): Promise<CandidateWithStudent | null> {
  const { data: student, error: sErr } = await client
    .from('students')
    .select(
      `
      phone,
      name,
      course,
      attempt,
      city,
      email,
      exp_band,
      status,
      candidates (
        id,
        name,
        current_position,
        experience_years,
        exp_band,
        education,
        skills,
        resume_url,
        linkedin_url,
        availability,
        placement_status,
        profile_score,
        reliability_score,
        total_applications,
        total_shortlists,
        total_placements,
        created_at,
        updated_at
      ),
      student_profiles (
        phone,
        company,
        role,
        city,
        graduation_year,
        linkedin_url,
        avatar_url,
        exp_band
      )
    `
    )
    .eq('phone', phone)
    .is('deleted_at', null)
    .maybeSingle()

  if (sErr) throw sErr
  if (!student) return null

  return mapStudentRow(student as StudentWithCandidates)
}
